/**
 * Employee Onboarding/Offboarding Velocity
 *
 * Analyzes how quickly IT provisions new hires and recovers equipment from terminations.
 * KPIs are clickable with drilldown data.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface VelocityData {
  period: { days: number };
  totals: { newHires: number; terminations: number; newHiresWithHardware: number; newHiresNoHardware: number };
  provisioning: {
    avgHours: number | null; minHours: number | null; maxHours: number | null;
    within3Days: number; totalCompleted: number; readinessRate: number | null;
    distribution: Array<{ bucket: string; cnt: number }>;
  };
  termRecovery: {
    avgRecoveryHours: number | null; within7Days: number; totalCompleted: number;
    stillOpen: number; recoveryRate: number | null;
  };
  monthlyTrend: Array<{ month: string; newHires: number; terminations: number; netChange: number }>;
  weeklyVelocity: Array<{ weekStart: string; newHires: number; completed: number; avgDaysToClose: number | null }>;
  analystLoad: Array<{ analyst: string; newHireTickets: number; termTickets: number; computerDeployments: number; avgProvisionHours: number | null }>;
  dayOfWeekPattern: Array<{ dayName: string; dayNum: number; avgHires: number }>;
  pendingOnboarding: Array<{ ticketId: number; title: string; createdDate: string; daysOpen: number; analyst: string | null; statusName: string }>;
  pendingTerminations: Array<{ ticketId: number; title: string; createdDate: string; daysOpen: number; analyst: string | null; statusName: string }>;
  slowestOnboarding: Array<{ ticketId: number; title: string; createdDate: string; completedDate: string; daysToClose: number; analyst: string | null }>;
  generatedAt: string;
}

const PERIOD_OPTIONS = [
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
];

export function OnboardingVelocity() {
  const [days, setDays] = useState(90);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['onboarding-velocity', days],
    queryFn: () => apiClient.get<VelocityData>(`onboarding-velocity?days=${days}`),
  });

  if (isLoading) return <LoadingSpinner message="Loading onboarding data..." />;
  if (error) return <ErrorMessage message={(error as Error).message} />;
  if (!data) return null;

  const { totals, provisioning, termRecovery, monthlyTrend, weeklyVelocity, analystLoad, dayOfWeekPattern, pendingOnboarding, pendingTerminations, slowestOnboarding } = data;

  const toggleSection = (section: string) => setExpandedSection(prev => prev === section ? null : section);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Onboarding & Offboarding Velocity</h1>
          <p className="text-sm text-slate-500">How quickly IT provisions new hires and recovers equipment from terminations</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="py-2 px-3 rounded-lg border border-border text-sm bg-white">
          {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* ═══ KPIs ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Kpi label="New Hires" value={totals.newHires} onClick={() => toggleSection('pendingOnboarding')} />
        <Kpi label="With Hardware" value={totals.newHiresWithHardware} sub={`${totals.newHires > 0 ? Math.round((totals.newHiresWithHardware / totals.newHires) * 100) : 0}%`} />
        <Kpi label="Avg Provision Time" value={provisioning.avgHours ? `${Math.round(provisioning.avgHours / 24)}d` : '—'} color={provisioning.avgHours && provisioning.avgHours > 72 ? 'red' : undefined} />
        <Kpi label="Ready in 3 Days" value={provisioning.readinessRate != null ? `${provisioning.readinessRate}%` : '—'} color={provisioning.readinessRate != null && provisioning.readinessRate < 70 ? 'red' : undefined} />
        <Kpi label="Terminations" value={totals.terminations} onClick={() => toggleSection('pendingTerminations')} />
        <Kpi label="Recovered in 7d" value={termRecovery.recoveryRate != null ? `${termRecovery.recoveryRate}%` : '—'} color={termRecovery.recoveryRate != null && termRecovery.recoveryRate < 70 ? 'red' : undefined} />
      </div>

      {/* ═══ Provisioning Time Distribution ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-sm font-semibold text-text-primary mb-3">New Hire Provisioning Time</h3>
          <div className="space-y-2">
            {provisioning.distribution.map(d => {
              const max = Math.max(...provisioning.distribution.map(x => x.cnt), 1);
              const isLate = d.bucket.includes('5-7') || d.bucket.includes('7+');
              return (
                <div key={d.bucket} className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 w-20">{d.bucket}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full rounded ${isLate ? 'bg-red-400' : 'bg-emerald-400'}`} style={{ width: `${(d.cnt / max) * 100}%` }} />
                  </div>
                  <span className="text-xs font-medium text-slate-700 w-8 text-right">{d.cnt}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-border text-xs text-slate-500 flex gap-4">
            <span>Min: {provisioning.minHours ? `${Math.round(provisioning.minHours)}h` : '—'}</span>
            <span>Avg: {provisioning.avgHours ? `${Math.round(provisioning.avgHours)}h` : '—'}</span>
            <span>Max: {provisioning.maxHours ? `${Math.round(provisioning.maxHours / 24)}d` : '—'}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border p-5">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Termination Recovery</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-xl font-bold text-text-primary">{termRecovery.avgRecoveryHours ? `${Math.round(termRecovery.avgRecoveryHours / 24)}d` : '—'}</div>
              <div className="text-xs text-slate-500">Avg Recovery Time</div>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className={`text-xl font-bold ${termRecovery.recoveryRate != null && termRecovery.recoveryRate >= 70 ? 'text-emerald-600' : 'text-red-600'}`}>
                {termRecovery.recoveryRate ?? 0}%
              </div>
              <div className="text-xs text-slate-500">Within 7 Days</div>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-xl font-bold text-text-primary">{termRecovery.totalCompleted}</div>
              <div className="text-xs text-slate-500">Completed</div>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className={`text-xl font-bold ${termRecovery.stillOpen > 5 ? 'text-red-600' : 'text-amber-600'}`}>{termRecovery.stillOpen}</div>
              <div className="text-xs text-slate-500">Still Open</div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Monthly Trend (Seasonal Patterns) ═══ */}
      {monthlyTrend.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Monthly Hiring & Termination Trend</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Month</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">New Hires</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Terminations</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Net Change</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Trend</th>
              </tr></thead>
              <tbody>
                {monthlyTrend.map(m => (
                  <tr key={m.month} className="border-b border-border-subtle">
                    <td className="py-2 px-2 font-medium">{m.month}</td>
                    <td className="py-2 px-2 text-right text-emerald-600 font-medium">{m.newHires}</td>
                    <td className="py-2 px-2 text-right text-red-600 font-medium">{m.terminations}</td>
                    <td className={`py-2 px-2 text-right font-bold ${m.netChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {m.netChange >= 0 ? '+' : ''}{m.netChange}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1">
                        <div className="h-3 bg-emerald-200 rounded" style={{ width: `${Math.min(m.newHires * 3, 100)}px` }} />
                        <div className="h-3 bg-red-200 rounded" style={{ width: `${Math.min(m.terminations * 3, 100)}px` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Weekly Velocity ═══ */}
      {weeklyVelocity.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Weekly Onboarding Velocity</h3>
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-white"><tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Week</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">New Hires</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Completed</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Avg Days to Close</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Completion Rate</th>
              </tr></thead>
              <tbody>
                {weeklyVelocity.map(w => (
                  <tr key={w.weekStart} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2">{new Date(w.weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                    <td className="py-1.5 px-2 text-right">{w.newHires}</td>
                    <td className="py-1.5 px-2 text-right">{w.completed}</td>
                    <td className="py-1.5 px-2 text-right">{w.avgDaysToClose != null ? `${w.avgDaysToClose.toFixed(1)}d` : '—'}</td>
                    <td className="py-1.5 px-2 text-right">
                      <span className={w.newHires > 0 && w.completed / w.newHires >= 0.8 ? 'text-emerald-600' : w.newHires > 0 ? 'text-amber-600' : ''}>
                        {w.newHires > 0 ? `${Math.round((w.completed / w.newHires) * 100)}%` : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Per-Analyst Load ═══ */}
      {analystLoad.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Analyst Onboarding/Offboarding Load</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Analyst</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">New Hires</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Terminations</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Computer Deploys</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Avg Provision Time</th>
              </tr></thead>
              <tbody>
                {analystLoad.map(a => (
                  <tr key={a.analyst} className="border-b border-border-subtle">
                    <td className="py-2 px-2 font-medium">{a.analyst}</td>
                    <td className="py-2 px-2 text-right">{a.newHireTickets}</td>
                    <td className="py-2 px-2 text-right">{a.termTickets}</td>
                    <td className="py-2 px-2 text-right">{a.computerDeployments}</td>
                    <td className="py-2 px-2 text-right">
                      {a.avgProvisionHours != null ? (
                        <span className={a.avgProvisionHours > 72 ? 'text-red-600 font-bold' : ''}>
                          {Math.round(a.avgProvisionHours / 24)}d
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Day of Week Pattern ═══ */}
      {dayOfWeekPattern.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h3 className="text-sm font-semibold text-text-primary mb-3">New Hire Requests by Day of Week</h3>
          <p className="text-xs text-slate-500 mb-3">Average new hire tickets submitted per day — helps predict staffing needs</p>
          <div className="flex items-end gap-3 h-32">
            {dayOfWeekPattern.map(d => {
              const max = Math.max(...dayOfWeekPattern.map(x => x.avgHires), 1);
              return (
                <div key={d.dayNum} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-slate-700">{d.avgHires.toFixed(1)}</span>
                  <div className="w-full bg-baker-red/70 rounded-t" style={{ height: `${(d.avgHires / max) * 80}px` }} />
                  <span className="text-[10px] text-slate-500">{d.dayName.slice(0, 3)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ Pending Onboarding (expandable) ═══ */}
      {expandedSection === 'pendingOnboarding' && pendingOnboarding.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Open New Hire Tickets ({pendingOnboarding.length})</h3>
            <button onClick={() => setExpandedSection(null)} className="text-xs text-slate-400 hover:text-slate-600">Close</button>
          </div>
          <div className="overflow-y-auto max-h-[300px]">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-white"><tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Ticket</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Title</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Days Open</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Analyst</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Status</th>
              </tr></thead>
              <tbody>
                {pendingOnboarding.map(t => (
                  <tr key={t.ticketId} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2 text-baker-red font-medium">{t.ticketId}</td>
                    <td className="py-1.5 px-2 text-xs truncate max-w-[250px]">{t.title}</td>
                    <td className={`py-1.5 px-2 text-right font-medium ${t.daysOpen > 5 ? 'text-red-600' : ''}`}>{t.daysOpen}</td>
                    <td className="py-1.5 px-2 text-xs">{t.analyst || '—'}</td>
                    <td className="py-1.5 px-2 text-xs">{t.statusName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Pending Terminations (expandable) ═══ */}
      {expandedSection === 'pendingTerminations' && pendingTerminations.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Open Termination Tickets ({pendingTerminations.length})</h3>
            <button onClick={() => setExpandedSection(null)} className="text-xs text-slate-400 hover:text-slate-600">Close</button>
          </div>
          <div className="overflow-y-auto max-h-[300px]">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-white"><tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Ticket</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Title</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Days Open</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Analyst</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Status</th>
              </tr></thead>
              <tbody>
                {pendingTerminations.map(t => (
                  <tr key={t.ticketId} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2 text-baker-red font-medium">{t.ticketId}</td>
                    <td className="py-1.5 px-2 text-xs truncate max-w-[250px]">{t.title}</td>
                    <td className={`py-1.5 px-2 text-right font-medium ${t.daysOpen > 7 ? 'text-red-600' : ''}`}>{t.daysOpen}</td>
                    <td className="py-1.5 px-2 text-xs">{t.analyst || '—'}</td>
                    <td className="py-1.5 px-2 text-xs">{t.statusName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Slowest Onboarding (outliers) ═══ */}
      {slowestOnboarding.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Slowest Onboarding (outliers)</h3>
          <p className="text-xs text-slate-500 mb-3">Tickets that took the longest to close — identify bottlenecks</p>
          <div className="overflow-y-auto max-h-[250px]">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-white"><tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Title</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Days to Close</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Created</th>
                <th className="text-left py-2 px-2 font-medium text-slate-600">Analyst</th>
              </tr></thead>
              <tbody>
                {slowestOnboarding.map(t => (
                  <tr key={t.ticketId} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2 text-xs truncate max-w-[300px]">{t.title}</td>
                    <td className="py-1.5 px-2 text-right font-bold text-red-600">{t.daysToClose}d</td>
                    <td className="py-1.5 px-2 text-xs text-slate-500">{t.createdDate}</td>
                    <td className="py-1.5 px-2 text-xs">{t.analyst || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KPI Card ──────────────────────────────────────────────────────────

function Kpi({ label, value, sub, color, onClick }: { label: string; value: string | number; sub?: string; color?: 'red'; onClick?: () => void }) {
  return (
    <div className={`bg-white rounded-lg border border-border p-4 ${onClick ? 'cursor-pointer hover:border-baker-red/40 hover:shadow-sm transition-all' : ''}`} onClick={onClick}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color === 'red' ? 'text-red-700' : 'text-text-primary'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
      {onClick && <div className="text-[10px] text-slate-400 mt-1">Click to expand →</div>}
    </div>
  );
}
